import {
  ApplicationScope,
  Dimension,
  getNextEntityName,
  WidgetTypes,
  VariableType,
  VariableMode,
  refactorNameInValue,
} from "@superblocksteam/shared";
import { set, get, uniq, uniqBy, isPlainObject, isArray, merge } from "lodash";
import { v4 as uuidv4 } from "uuid";
import { CanvasLayout, GridDefaults } from "legacy/constants/WidgetConstants";
import WidgetConfigResponse from "legacy/mockResponses/WidgetConfigResponse";
import { getItemPropertyPaneConfig } from "legacy/pages/Editor/PropertyPane/ItemPropertyPaneConfig";
import {
  flattenObject,
  unflattenObject,
} from "legacy/pages/Editor/PropertyPane/widgetPropertyPaneConfigUtils";
import {
  calculateNewWidgetPosition,
  calculateNewWidgetPositionOnMousePosition,
} from "legacy/sagas/WidgetOperationsSagasUtils";
import { getDataTree } from "legacy/selectors/dataTreeSelectors";
import { getOccupiedSpacesSelectorForContainer } from "legacy/selectors/editorSelectors";
import {
  getWidgetDynamicPropertyPathList,
  mergeUpdatesWithBindingsOrTriggers,
} from "legacy/utils/DynamicBindingUtils";
import { getWidgetTypeFromSimplifiedType } from "legacy/utils/ai";
import { generateReactKey } from "legacy/utils/generators";
import { extractJsEvaluationPairs } from "legacy/workers/evaluationUtils";
import { fastClone } from "utils/clone";
import { findClosestCanvas } from "utils/paste";
import { Flag } from "../featureFlags";
import { getColumnIdFromAi, getKvPropertyIdFromAi } from "./columnUtils";
import { getEventHandlers } from "./eventHandlers";
import {
  removeKvItem,
  addKvItem,
  updateKvItem,
  updatePropertiesOrder,
} from "./keyValueUtils";
import {
  addMenuItem,
  initializeMenuMetadata,
  removeMenuItem,
  updateMenuItem,
} from "./menuUtils";
import {
  addSectionColumn,
  initializeSectionColumnMetadata,
  removeSectionColumn,
  updateSectionColumn,
} from "./sectionUtils";
import {
  addTab,
  initializeTabMetadata,
  removeTab,
  updateTab,
} from "./tabUtils";
import {
  addColumns,
  removeColumns,
  updateColumnOrder,
  updateColumns,
} from "./tableUtils";
import { sanitizeEdits } from "./utils";
import type {
  AddEventActionValue,
  AddColumnEventAction,
  ClarkComponentAction,
  ClarkAction,
  ClarkCreateApiAction,
  ColumnAction,
  DiscardedEdit,
  EditMetadata,
  KvAction,
  MenuItemAction,
  RemoveColumnEventAction,
  RemoveEventAction,
  RemoveTabAction,
  RemoveTabPropertyAction,
  SetColumnAction,
  SetTabAction,
  TabAction,
  ClarkEditApiAction,
  ClarkActionFuntions,
  SectionColumnAction,
  ClarkScaffoldApiAction,
  ClarkEditEventAction,
  ClarkCreateEventAction,
  ClarkEditStateVarAction,
  ClarkCreateTimerAction,
  ClarkCreateStateVarAction,
  ClarkEditTimerAction,
  MoveComponentAction,
} from "./types";
import type { Flags } from "../featureFlags";
import type {
  RouteDef,
  BaseBlock,
  ParallelConfig,
  ConditionalConfig,
  LoopConfig,
  TryCatchConfig,
  VariablesConfig,
  StreamConfig,
} from "@superblocksteam/shared";
import type { createNameValidator } from "hooks/store/useEntityNameValidator";
import type { PropertyPaneConfig } from "legacy/constants/PropertyControlConstants";
import type { DataTreeWidget } from "legacy/entities/DataTree/dataTreeFactory";
import type { DataTree } from "legacy/entities/DataTree/dataTreeFactory";
import type { ItemWithPropertiesType } from "legacy/pages/Editor/PropertyPane/ItemKindConstants";
import type { FlattenedWidgetProps } from "legacy/reducers/entityReducers/canvasWidgetsReducer";
import type { WidgetMetadata } from "legacy/reducers/entityReducers/metaReducer";
import type { APP_MODE } from "legacy/reducers/types";
import type { getDynamicLayoutWidgets } from "legacy/selectors/layoutSelectors";
import type { GeneratedTheme } from "legacy/themes";
import type { WidgetProps } from "legacy/widgets";
import type { CanvasWidgetsReduxState } from "legacy/widgets/Factory";
import type { TableWidgetProps } from "legacy/widgets/TableWidget/TableWidgetConstants";
import type { FlattenedWidgetLayoutProps } from "legacy/widgets/shared";
import type { ApiDto } from "store/slices/apis/types";
import type { ApiDtoWithPb } from "store/slices/apisV2/slice";
import type { AppState } from "store/types";

type ProcessingContext = {
  clarkRequestId: string;
  dataTree: DataTree;
  routes: RouteDef[];
  nameValidator: ReturnType<typeof createNameValidator>;
  featureFlags: Flags;
  theme: GeneratedTheme;
  canvasWidgets: Record<string, Partial<WidgetProps>>;
  apisV2: Record<string, ApiDtoWithPb>;
  focusedEntityId: string;
  // focusedEntityType: ClarkFocusedEntityType;
  appMode: APP_MODE;
  dynamicWidgetLayout: ReturnType<typeof getDynamicLayoutWidgets>;
  widgetsRuntime: CanvasWidgetsReduxState;
  widgetMetaProps: WidgetMetadata;
  evaluatedWidgets: Record<string, DataTreeWidget>;
  previousAiWidgets: Record<string, DataTreeWidget>;
  applicationId: string;
  organizationId: string;
  pageId: string;
};

export type ClarkProcessorResult = {
  // entity type specific changes
  widgetChangedKeys?: Record<string, string[]>;
  widgetChanges: Record<string, Record<string, unknown> | null>;
  widgetMovesById?: Record<
    string,
    {
      position: "left" | "right" | "above" | "below" | "inside";
      relativeTo: string;
    }
  >;
  apiChanges?: Record<string, ApiDtoWithPb>;
  changedApiBlockPaths?: Record<string, string[]>;
  stateVariableChanges?: Record<string, Record<string, unknown> | null>;
  stateVariableChangedKeys?: Record<string, string[]>;
  timerChanges?: Record<string, Record<string, unknown> | null>;
  timerChangedKeys?: Record<string, string[]>;
  eventChanges?: Record<string, Record<string, unknown> | null>;
  eventChangedKeys?: Record<string, string[]>;

  // not specific to an entity type
  renamesById?: Record<string, string>;
  originalNamesById?: Record<string, string>;
  discardedEdits: Record<string, DiscardedEdit[]>;
  metadataByWidgetId?: Record<string, EditMetadata | undefined>;
};

const ORDER_KEYS: Record<
  string,
  {
    updater: (args: {
      orderFromAi: string[];
      existingOrder: string[];
      originalItems: Record<string, any>;
    }) => string[];
    originalItemsKey: string;
  }
> = {
  columnOrder: {
    updater: updateColumnOrder,
    originalItemsKey: "primaryColumns",
  },
  propertiesOrder: {
    updater: updatePropertiesOrder,
    originalItemsKey: "properties",
  },
};

interface ItemHandlerProps<T> {
  action: T;
  itemId: string | number;
  itemExists: boolean;
  canvasWidget: Partial<WidgetProps>;
  widgetId: string;
  itemCount: number;
}

type ItemIdAndExistence =
  | {
      itemId: string;
      type: "column";
      exists: boolean;
    }
  | {
      itemId: number;
      type: "menu_item";
      exists: boolean;
    }
  | {
      itemId: number;
      type: "tab";
      exists: boolean;
    }
  | {
      itemId: string;
      type: "key_value_property";
      exists: boolean;
    }
  | {
      itemId: number;
      type: "section_column";
      exists: boolean;
    }
  | {
      itemId: undefined;
      type: undefined;
      exists: false;
    };

const METADATA_INITIALIZERS: Partial<
  Record<WidgetTypes, (widget: Partial<WidgetProps>) => EditMetadata>
> = {
  [WidgetTypes.TABS_WIDGET]: initializeTabMetadata,
  [WidgetTypes.MENU_WIDGET]: initializeMenuMetadata,
  [WidgetTypes.SECTION_WIDGET]: initializeSectionColumnMetadata,
};

type ClarkApiBlockSpec = {
  name: string;
  shape: string;
  block: ClarkApiControlBlockSpec | ClarkApiStepBlockSpec;
};

// this almost follows the BaseBlock type, but it wraps all block configs in a "block" property
type ClarkApiControlBlockSpec = {
  name: string;
  shape: string;
  block: {
    [key: string]: unknown;
  };
};

type ClarkApiStepBlockSpec = {
  integration: {
    name: string;
    type: string;
    instruction: string;
  };
};

/**
 * ClarkProcessor handles AI-driven modifications to widget properties.
 *
 * This class processes changes requested by AI actions and applies them to the widget tree.
 * It tracks specific paths that were modified and maintains metadata needed for
 * complex widget modifications like tabs, menu items, and table columns.
 *
 */

export class ClarkProcessor {
  private _clarkRequestId: string;
  // widget changes is the result of sanitizing and consolidating nestedChangesByWidgetId
  private widgetChanges: Record<string, Record<string, unknown> | null>;
  private widgetChangedKeys: Record<string, string[]>;
  // Nested structure for storing widget changes during processing
  private nestedChangesByWidgetId: Record<
    string,
    Record<string, unknown> | null
  >;

  private apiChanges: Record<ApiDto["id"], ApiDtoWithPb>;
  private changedApiBlockPaths: Record<string, Set<string>>;

  private stateVariableChanges: Record<string, Record<string, unknown> | null>;
  private stateVariableChangedKeys: Record<string, string[]>;

  private timerChanges: Record<string, Record<string, unknown> | null>;
  private timerChangedKeys: Record<string, string[]>;

  private eventChanges: Record<string, Record<string, unknown> | null>;
  private eventChangedKeys: Record<string, string[]>;

  private processedActions: Set<string>;
  private createdEntityNamesToIds: Record<string, string>;
  private renamesById: Record<string, string>;
  private originalNamesById: Record<string, string>;
  private discardedEdits: Record<string, DiscardedEdit[]>;
  private metadataByWidgetId: Record<string, EditMetadata | undefined>;
  private context!: ProcessingContext;
  private allEntityNames: string[];
  private widgetMovesById: Record<
    string,
    {
      position: "left" | "right" | "above" | "below" | "inside";
      relativeTo: string;
    }
  >;

  // functions
  private createComponentFn: ClarkActionFuntions["createComponentFn"];
  private updateComponentFn: ClarkActionFuntions["updateComponentFn"];
  private deleteComponentFn: ClarkActionFuntions["deleteComponentFn"];
  private deleteComponentPropertiesFn: ClarkActionFuntions["deleteComponentPropertiesFn"];
  private renameEntityFn: ClarkActionFuntions["renameEntityFn"];
  private createApiFn: ClarkActionFuntions["createApiFn"];
  private updateApiFn: ClarkActionFuntions["updateApiFn"];
  private addSectionColumnFn: ClarkActionFuntions["addSectionColumnFn"];
  private createStateVarFn: ClarkActionFuntions["createStateVarFn"];
  private editStateVarFn: ClarkActionFuntions["editStateVarFn"];
  private createTimerFn: ClarkActionFuntions["createTimerFn"];
  private editTimerFn: ClarkActionFuntions["editTimerFn"];
  private createEventFn: ClarkActionFuntions["createEventFn"];
  private editEventFn: ClarkActionFuntions["editEventFn"];
  private moveWidgetFn: ClarkActionFuntions["moveWidgetFn"];
  private resizeComponentFn: ClarkActionFuntions["resizeComponentFn"];
  private getState: () => AppState;

  public get clarkRequestId(): string {
    return this._clarkRequestId;
  }

  private updateContextFromRedux() {
    this.context.canvasWidgets = this.getState().legacy.entities.canvasWidgets;
    this.context.apisV2 = this.getState().apisV2.entities as Record<
      string,
      ApiDtoWithPb
    >;
    this.context.dataTree = getDataTree(this.getState());
  }

  constructor(
    context: ProcessingContext,
    getState: () => AppState,
    actionFunctions: ClarkActionFuntions,
  ) {
    this._clarkRequestId = context.clarkRequestId;
    this.context = context;
    this.getState = getState;
    this.widgetChanges = {};
    this.widgetChangedKeys = {};
    this.widgetMovesById = {};
    this.renamesById = {};
    this.originalNamesById = {};
    this.discardedEdits = {};
    this.metadataByWidgetId = {};
    this.nestedChangesByWidgetId = {};
    this.stateVariableChanges = {};
    this.stateVariableChangedKeys = {};
    this.timerChanges = {};
    this.timerChangedKeys = {};
    this.eventChanges = {};
    this.eventChangedKeys = {};
    this.context = context;
    this.createdEntityNamesToIds = {};
    this.apiChanges = {}; // apiId -> apiSpec
    this.changedApiBlockPaths = {}; // apiId -> path, eg: "blocks.{blockName}.propertyName"
    this.processedActions = new Set();
    this.allEntityNames = [
      ...Object.values(context.canvasWidgets).map(
        (widget) => widget.widgetName as string,
      ),
      ...Object.keys(context.dataTree),
      ...Object.values(context.apisV2).map((api) => api.apiPb?.metadata.name),
    ];

    this.createComponentFn = actionFunctions.createComponentFn;
    this.updateComponentFn = actionFunctions.updateComponentFn;
    this.deleteComponentFn = actionFunctions.deleteComponentFn;
    this.deleteComponentPropertiesFn =
      actionFunctions.deleteComponentPropertiesFn;
    this.renameEntityFn = actionFunctions.renameEntityFn;
    this.createApiFn = actionFunctions.createApiFn;
    this.updateApiFn = actionFunctions.updateApiFn;
    this.addSectionColumnFn = actionFunctions.addSectionColumnFn;
    this.createStateVarFn = actionFunctions.createStateVarFn;
    this.editStateVarFn = actionFunctions.editStateVarFn;
    this.createTimerFn = actionFunctions.createTimerFn;
    this.editTimerFn = actionFunctions.editTimerFn;
    this.createEventFn = actionFunctions.createEventFn;
    this.editEventFn = actionFunctions.editEventFn;
    this.moveWidgetFn = actionFunctions.moveWidgetFn;
    this.resizeComponentFn = actionFunctions.resizeComponentFn;
  }

  public getResult(): ClarkProcessorResult {
    // Don't return direct references as redux can freeze these objects
    return fastClone({
      widgetChanges: this.widgetChanges,
      widgetChangedKeys: this.widgetChangedKeys,
      renamesById: this.renamesById,
      originalNamesById: this.originalNamesById,
      discardedEdits: this.discardedEdits,
      metadataByWidgetId: this.metadataByWidgetId,
      apiChanges: this.apiChanges,
      changedApiBlockPaths: Object.fromEntries(
        Object.entries(this.changedApiBlockPaths).map(([key, value]) => [
          key,
          Array.from(value),
        ]),
      ),
      stateVariableChanges: this.stateVariableChanges,
      stateVariableChangedKeys: this.stateVariableChangedKeys,
      timerChanges: this.timerChanges,
      timerChangedKeys: this.timerChangedKeys,
      eventChanges: this.eventChanges,
      eventChangedKeys: this.eventChangedKeys,
      widgetMovesById: this.widgetMovesById,
    });
  }

  public async addAction(action: ClarkAction, defaultComponentName: string) {
    this.updateContextFromRedux();

    const { action: actionType, id } = action;

    if (this.processedActions.has(id)) {
      return;
    }

    this.processedActions.add(id);

    // api actions
    if (actionType === "completeApi") {
      // nothing to do here
      return;
    }

    if (actionType === "createApi") {
      const { apiName } = action as ClarkCreateApiAction;

      const newApiId = uuidv4();

      // we use the original api name so we can do lookups for edits later
      this.createdEntityNamesToIds[apiName] = newApiId;

      let apiNameToUse = apiName;
      if (this.allEntityNames.includes(apiNameToUse)) {
        apiNameToUse = getNextEntityName(apiNameToUse, this.allEntityNames);
      }

      this.allEntityNames.push(apiNameToUse);

      const newApiV2 = this.createApiChanges({
        apiId: newApiId,
        apiName: apiNameToUse,
        blocks: [],
      });

      this.createApiFn(newApiV2);

      return;
    }

    if (actionType === "scaffoldApi") {
      // should already exist inside of context as an existing api
      // or inside the createdEntityNamesToIds from createApi
      const { apiName: apiNameFromAction, apiSpec } =
        action as ClarkScaffoldApiAction;

      let apiId = this.createdEntityNamesToIds[apiNameFromAction];
      let apiName: string = apiNameFromAction;

      let api = Object.values(this.context.apisV2).find((api) => {
        if (apiId) {
          return api.apiPb?.metadata.id === apiId;
        }

        return api.apiPb?.metadata.name === apiName;
      });

      if (!api) {
        api = this.apiChanges[apiId];
      }

      apiId = api.apiPb?.metadata.id;
      apiName = api.apiPb?.metadata.name;

      for (const block of apiSpec) {
        const changedPaths = ClarkProcessor.getClarkApiBlockPaths(block.block);
        for (const path of changedPaths) {
          if (!this.changedApiBlockPaths[apiId]) {
            this.changedApiBlockPaths[apiId] = new Set();
          }
          this.changedApiBlockPaths[apiId].add(`blocks.${block.name}.${path}`);
        }
      }

      const updatedApiV2 = this.createApiChanges({
        apiId,
        apiName,
        blocks: apiSpec as any,
      });
      this.updateApiFn(updatedApiV2);
      return;
    }

    if (actionType === "editApi") {
      const { apiName, stepName, stepEdit } = action as ClarkEditApiAction;
      // We assume the api has been created and is redux
      // at this point, and so is available in the context
      // otherwise, it should be in the createdEntityNamesToIds
      const apiIdForLookup = this.createdEntityNamesToIds[apiName];

      const apiId: string | undefined = Object.values(this.context.apisV2).find(
        (api) => {
          if (apiIdForLookup) {
            return api.apiPb?.metadata.id === apiIdForLookup;
          }

          return api.apiPb?.metadata.name === apiName;
        },
      )?.apiPb?.metadata?.id;

      if (!apiId) {
        console.error(`Api not found for api with name: ${apiName}`);
        return;
      }

      // Initialize the Set for this API ID if it doesn't exist
      if (!this.changedApiBlockPaths[apiId]) {
        this.changedApiBlockPaths[apiId] = new Set();
      }

      const api = this.context.apisV2[apiId];

      if (!api) {
        throw new Error(`Api not found for api id: ${apiId}`);
      }

      // Sync current api to apiChanges. cloning to unfreeze any nested objects
      this.apiChanges[apiId] = fastClone(this.apiChanges[apiId] ?? api);
      this.findAndUpdateApiBlock(apiId, stepName, stepEdit);
      this.changedApiBlockPaths[apiId].add(
        `blocks.${stepName}.${stepEdit.property}`,
      );
      this.updateApiFn(this.apiChanges[apiId]);
      return;
    }

    // state var actions
    if (actionType === "createStateVar") {
      const { variableName } = action as ClarkCreateStateVarAction;
      const variableId = uuidv4();
      this.createdEntityNamesToIds[variableName] = variableId;
      this.createStateVarFn(variableName, variableId);
      this.stateVariableChanges[variableId] = {};
      return;
    }
    if (actionType === "editStateVar") {
      const { variableName, property, value } =
        action as ClarkEditStateVarAction;
      const variableId =
        this.createdEntityNamesToIds[variableName] ??
        (this.context.dataTree[ApplicationScope.PAGE][variableName] as any)?.id;
      set(this.stateVariableChanges, `${variableId}.${property}`, value);
      if (!this.stateVariableChangedKeys[variableId]) {
        this.stateVariableChangedKeys[variableId] = [];
      }
      this.stateVariableChangedKeys[variableId].push(property);
      this.editStateVarFn({
        variableName,
        variableId: variableId ?? variableName,
        property,
        value,
      });
      return;
    }

    // timer actions
    if (actionType === "createTimer") {
      const { timerName } = action as ClarkCreateTimerAction;
      const timerId = uuidv4();
      this.createdEntityNamesToIds[timerName] = timerId;
      this.createTimerFn(timerName, timerId);
      this.timerChanges[timerId] = {};
      return;
    }
    if (actionType === "editTimer") {
      const { timerName, property, value } = action as ClarkEditTimerAction;
      const timerId =
        this.createdEntityNamesToIds[timerName] ??
        (this.context.dataTree[ApplicationScope.PAGE][timerName] as any)?.id;
      if (property === "onFire") {
        const existingSteps =
          this.timerChanges[timerId]?.steps ??
          (this.context.dataTree[ApplicationScope.PAGE][timerName] as any)
            ?.steps ??
          [];
        const newSteps = getEventHandlers({
          prevValue: existingSteps,
          value: value as AddEventActionValue,
          dataTree: this.context.dataTree,
          routes: this.context.routes,
        });
        set(this.timerChanges, `${timerId}.steps`, newSteps);
        this.editTimerFn({
          timerName,
          timerId: timerId ?? timerName,
          property: "steps",
          value: newSteps,
        });
        if (this.timerChangedKeys[timerId]) {
          this.timerChangedKeys[timerId].push("steps");
        } else {
          this.timerChangedKeys[timerId] = ["steps"];
        }
      } else {
        this.editTimerFn({
          timerName,
          timerId: timerId ?? timerName,
          property,
          value,
        });
        set(this.timerChanges, `${timerId}.${property}`, value);
        if (this.timerChangedKeys[timerId]) {
          this.timerChangedKeys[timerId].push(property);
        } else {
          this.timerChangedKeys[timerId] = [property];
        }
      }
      return;
    }

    // event actions
    if (actionType === "createEvent") {
      const { eventName } = action as ClarkCreateEventAction;
      const eventId = uuidv4();
      this.createdEntityNamesToIds[eventName] = eventId;
      this.createEventFn(eventName, eventId);
      this.eventChanges[eventId] = {};
      return;
    }
    if (actionType === "editEvent") {
      const { eventName, property, value } = action as ClarkEditEventAction;
      const eventId =
        this.createdEntityNamesToIds[eventName] ??
        (this.context.dataTree[ApplicationScope.PAGE][eventName] as any)?.id;
      if (!this.eventChangedKeys[eventId]) {
        this.eventChangedKeys[eventId] = [];
      }
      this.eventChangedKeys[eventId].push(property);

      if (property === "onTrigger") {
        const existingSteps =
          this.eventChanges[eventId]?.onTrigger ??
          (this.context.dataTree[ApplicationScope.PAGE][eventName] as any)
            ?.onTrigger ??
          [];
        const newSteps = getEventHandlers({
          prevValue: existingSteps,
          value: value as AddEventActionValue,
          dataTree: this.context.dataTree,
          routes: this.context.routes,
        });
        this.editEventFn({
          eventName,
          eventId: eventId ?? eventName,
          property,
          value: newSteps,
        });
        set(this.eventChanges, `${eventId}.onTrigger`, newSteps);
      } else if (property === "arguments") {
        const existingArguments =
          this.eventChanges[eventId]?.arguments ??
          (this.context.dataTree[ApplicationScope.PAGE][eventName] as any)
            ?.arguments ??
          [];
        const newArguments = Array.isArray(value)
          ? value.map((arg) => ({
              name: arg.name,
              id: uuidv4(),
            }))
          : [];
        const allArguments = [...existingArguments, ...newArguments];
        set(this.eventChanges, `${eventId}.arguments`, allArguments);
        this.editEventFn({
          eventName,
          eventId: eventId ?? eventName,
          property,
          value: allArguments,
        });
        set(this.eventChanges, `${eventId}.arguments`, allArguments);
      }
      return;
    }

    // component actions
    if (actionType === "createComponent") {
      const widgetType = getWidgetTypeFromSimplifiedType(action.componentType);

      const parentId = findClosestCanvas(
        this.context.focusedEntityId,
        this.context.canvasWidgets as CanvasWidgetsReduxState,
      )?.widgetId;

      if (!parentId) {
        console.error(
          `Could not create widget ${action.componentName} because the selected component can't have children`,
        );
        return;
      }
      const newComponentId = generateReactKey();

      const originalName = action.componentName;
      let componentNameToUse = originalName;
      if (this.allEntityNames.includes(componentNameToUse)) {
        componentNameToUse = getNextEntityName(
          componentNameToUse,
          this.allEntityNames,
        );
        this.originalNamesById[newComponentId] = originalName;
      }

      // Initialize the new widget plus any children
      // into nestedChangesByWidgetId
      this.createDefaultWidgetAndChildWidgets(
        newComponentId,
        widgetType as WidgetTypes,
        componentNameToUse,
        parentId,
      );

      const parentWidget =
        this.nestedChangesByWidgetId[parentId] ??
        this.context.canvasWidgets[parentId];

      // Update the parent's children array
      const updatedChildren = [
        ...((parentWidget?.children as Array<string>) ?? []),
        newComponentId,
      ];

      // Create or update parent widget structure
      if (!this.nestedChangesByWidgetId[parentId]) {
        this.nestedChangesByWidgetId[parentId] = {};
      }

      set(
        this.nestedChangesByWidgetId[parentId] as Record<string, unknown>,
        "children",
        updatedChildren,
      );

      if (!this.widgetChangedKeys[parentId]) {
        this.widgetChangedKeys[parentId] = [];
      }
      this.widgetChangedKeys[parentId].push("children");
      this.createdEntityNamesToIds[originalName] = newComponentId; // indexed by the original name given by Clark so can be found on followup actions

      // Dispatch the create
      this.createComponentFn({
        parentWidgetId: parentId,
        widgetType: widgetType as WidgetTypes,
        newWidgetId: newComponentId,
        initialProps: fastClone(
          this.nestedChangesByWidgetId[newComponentId],
        ) as Partial<WidgetProps>,
      });
      this.renamesById[newComponentId] = componentNameToUse;
    } else if (actionType === "removeComponent") {
      // get the widget id
      const existingWidget = Object.values(this.context.canvasWidgets).find(
        (w) => w.widgetName === action.componentName,
      );

      if (!existingWidget) {
        console.error(
          `Cannot remove widget. Widget with name "${action.componentName}" not found in dataTree`,
        );
        return;
      }

      const widgetId = existingWidget.widgetId as string;
      const parentId = existingWidget.parentId as string;

      this.nestedChangesByWidgetId[widgetId] = null;

      // and remove from the parent children
      const prevParentWidget =
        this.nestedChangesByWidgetId[parentId] ??
        this.context.canvasWidgets[parentId];

      // Update the parent's children array
      const updatedChildren = (
        prevParentWidget?.children as Array<string>
      ).filter((child) => child !== widgetId);

      // Create or update parent widget structure
      if (!this.nestedChangesByWidgetId[parentId]) {
        this.nestedChangesByWidgetId[parentId] = {};
      }

      set(
        this.nestedChangesByWidgetId[parentId] as Record<string, unknown>,
        "children",
        updatedChildren,
      );

      if (!this.widgetChangedKeys[parentId]) {
        this.widgetChangedKeys[parentId] = [];
      }
      this.widgetChangedKeys[parentId].push("children");

      // Dispatch the delete
      this.deleteComponentFn({
        widgetId,
      });
    } else if (actionType === "moveComponent") {
      const { componentName, where, relativeTo } =
        action as MoveComponentAction;
      const existingWidget = Object.values(this.context.canvasWidgets).find(
        (w) => w.widgetName === componentName,
      );
      const widgetId = existingWidget
        ? existingWidget.widgetId
        : this.createdEntityNamesToIds[componentName];
      if (!widgetId) {
        console.error(
          `Widget with name "${componentName}" not found in dataTree`,
        );
        return;
      }
      const relativeToWidget = Object.values(this.context.canvasWidgets).find(
        (w) => w.widgetName === relativeTo,
      );
      const relativeToWidgetId = relativeToWidget
        ? relativeToWidget.widgetId
        : this.createdEntityNamesToIds[relativeTo];
      if (!relativeToWidgetId) {
        console.error(`Widget with name "${relativeTo}" not found in dataTree`);
        return;
      }
      this.moveWidgetToNewPosition(widgetId, where, relativeToWidgetId);
    } else {
      // Not create or remove, so it must be an edit
      const componentName = action.componentName ?? defaultComponentName;
      let dataTreeWidget = this.context.dataTree[ApplicationScope.PAGE][
        componentName
      ] as Partial<WidgetProps>;

      if (this.createdEntityNamesToIds[componentName]) {
        const createdWidgetId = this.createdEntityNamesToIds[componentName];
        dataTreeWidget = this.nestedChangesByWidgetId[
          createdWidgetId
        ] as Partial<WidgetProps>;
      }

      if (!dataTreeWidget.widgetId) {
        throw new Error(`Widget with name "${componentName}" has no widgetId`);
      }
      const widgetId = dataTreeWidget.widgetId;

      const canvasWidget =
        this.context.canvasWidgets[widgetId] ??
        // this case handles widgets that were created by AI within this session
        this.nestedChangesByWidgetId[widgetId];

      if (!this.nestedChangesByWidgetId[widgetId]) {
        this.nestedChangesByWidgetId[widgetId] = {};
      }
      if (!this.discardedEdits[widgetId]) {
        this.discardedEdits[widgetId] = [];
      }
      if (!this.widgetChangedKeys[widgetId]) {
        this.widgetChangedKeys[widgetId] = [];
      }
      const { items, itemCount } = this.getItems(canvasWidget);

      if (
        !this.metadataByWidgetId[widgetId] &&
        METADATA_INITIALIZERS[canvasWidget.type as WidgetTypes]
      ) {
        this.metadataByWidgetId[widgetId] =
          METADATA_INITIALIZERS[
            canvasWidget.type as keyof typeof METADATA_INITIALIZERS
          ]?.(canvasWidget);
      }

      const {
        itemId,
        exists: itemExists,
        type: itemType,
      } = this.getItemIdAndExistence({
        action,
        widget: canvasWidget,
        metadata: this.metadataByWidgetId[widgetId],
        items,
        changes: this.nestedChangesByWidgetId[widgetId]!,
      });
      const propertyName = this.getPropertyName(action, canvasWidget.type);

      // Use lodash get to safely access the nested property
      const previousValue = propertyName
        ? ((this.nestedChangesByWidgetId[widgetId]
            ? get(this.nestedChangesByWidgetId[widgetId], propertyName)
            : undefined) ?? canvasWidget[propertyName as keyof WidgetProps])
        : undefined;

      const valueFromAi = "value" in action ? action.value : undefined;
      let valueFromAiWithRenames = valueFromAi;
      if (valueFromAi) {
        for (const [id, oldName] of Object.entries(this.originalNamesById)) {
          const newName = this.renamesById[id];

          // Rename any kind of properties that use bindings
          valueFromAiWithRenames = await refactorNameInValue({
            oldValue: valueFromAiWithRenames,
            oldName,
            newName,
            dataTree: {
              ...this.context.dataTree,
              [oldName]: {}, // the key is required for the refactoring algorithm
            },
            extractPairs: extractJsEvaluationPairs,
          });

          // Refactor specific property where we reference a component by its name without bindings
          if (
            typeof valueFromAiWithRenames === "object" &&
            valueFromAiWithRenames !== null &&
            "component" in valueFromAiWithRenames
          ) {
            if (valueFromAiWithRenames.component === oldName) {
              valueFromAiWithRenames.component = newName;
            }
          }
        }
      }

      if (itemType) {
        // handle changes to tabs, columns, menu items, key value properties
        switch (itemType) {
          case "tab":
            this.handleTabActions({
              action: action as TabAction,
              itemId,
              itemExists,
              canvasWidget,
              widgetId,
              itemCount,
            });
            break;
          case "section_column":
            this.handleSectionColumnActions({
              action: action as SectionColumnAction,
              itemId,
              itemExists,
              canvasWidget,
              widgetId,
              itemCount,
            });
            break;
          case "column":
            this.handleColumnActions({
              action: action as ColumnAction,
              itemId,
              itemExists,
              canvasWidget,
              widgetId,
              itemCount,
            });
            break;
          case "menu_item":
            this.handleMenuItemActions({
              action: action as MenuItemAction,
              itemId,
              itemExists,
              canvasWidget,
              widgetId,
              itemCount,
            });
            break;
          case "key_value_property":
            this.handleKvActions({
              action: action as KvAction,
              itemId,
              itemExists,
              canvasWidget,
              widgetId,
              itemCount,
            });
            break;
        }
      } else {
        switch (actionType) {
          case "set":
            if (ORDER_KEYS[propertyName]) {
              const { updater, originalItemsKey } = ORDER_KEYS[propertyName];
              const existingOrder: string[] = this.nestedChangesByWidgetId[
                widgetId
              ]
                ? get(this.nestedChangesByWidgetId[widgetId], propertyName)
                : (canvasWidget as any)[propertyName];
              const newOrder = updater({
                orderFromAi: valueFromAi as string[],
                existingOrder: existingOrder as string[],
                originalItems: canvasWidget[
                  originalItemsKey as keyof WidgetProps
                ] as Record<string, any>,
              });
              this.setNestedChangeForWidget(widgetId, propertyName, newOrder);
            } else if (propertyName === "widgetName") {
              if (typeof valueFromAi === "string") {
                const val = valueFromAi.replaceAll(" ", "_");
                const nameError = this.context.nameValidator({
                  currentName: canvasWidget.widgetName as string,
                  name: val,
                  scope: ApplicationScope.PAGE,
                });
                if (!nameError) {
                  this.renamesById[widgetId] = val;
                  this.setNestedChangeForWidget(widgetId, propertyName, val);
                  this.renameEntityFn({
                    entityId: widgetId,
                    oldName: canvasWidget.widgetName as string,
                    newName: val,
                  });
                }
              }
            } else {
              this.setNestedChangeForWidget(
                widgetId,
                propertyName,
                valueFromAiWithRenames,
              );
            }
            break;
          case "add":
            this.setNestedChangeForWidget(
              widgetId,
              propertyName,
              getEventHandlers({
                prevValue: previousValue,
                value: valueFromAiWithRenames as
                  | AddEventActionValue
                  | AddEventActionValue[],
                dataTree: this.context.dataTree,
                routes: this.context.routes,
              }),
            );
            break;
          case "remove":
            if (valueFromAi != null) {
              const idToRemove = (valueFromAi as RemoveEventAction["value"])
                ?.id;
              if (Array.isArray(previousValue)) {
                this.setNestedChangeForWidget(
                  widgetId,
                  propertyName,
                  previousValue.filter((item) => item.id !== idToRemove),
                );
              }
            } else {
              this.setNestedChangeForWidget(widgetId, propertyName, undefined);
            }
            break;
          case "reset":
            this.setNestedChangeForWidget(widgetId, propertyName, undefined);
            break;
          default:
            console.error(`Unknown action type ${actionType} returned`);
        }
      }
      // Create widgetChanges for the widget
      this.createChangesForWidget(widgetId);
      const flattenedWidgetUpdates = flattenObject(
        fastClone(this.widgetChanges[widgetId] ?? {}),
      );
      // Dispatch the update
      this.updateComponentFn({
        widgetId,
        widgetUpdates: flattenedWidgetUpdates,
      });
    }
  }

  public consolidateWidgetChanges() {
    // clear out existing dataTreeChanges before we compute new ones
    this.widgetChanges = {};

    for (const widgetId of Object.keys(this.nestedChangesByWidgetId)) {
      this.createChangesForWidget(widgetId);
    }
  }

  private createChangesForWidget(widgetId: string) {
    // clear out existing dataTreeChanges before we compute new ones
    if (!this.widgetChanges) {
      this.widgetChanges = {};
    }
    this.widgetChanges[widgetId] = {};

    let existingWidget = this.context.canvasWidgets[widgetId];

    if (this.nestedChangesByWidgetId[widgetId] === null) {
      // Widget was deleted, use null to indicate deletion
      this.widgetChanges[widgetId] = null;

      // also remove the widget from the parent
      const parentId = existingWidget?.parentId;
      if (parentId) {
        const parentWidget = this.context.canvasWidgets[parentId];
        if (parentWidget) {
          // Use the nestedChangesByWidgetId given there may be a pending change to the parent's children
          const parentChildren = (this.nestedChangesByWidgetId[parentId]
            ?.children ??
            parentWidget.children ??
            []) as string[];
          const newParentChildren = parentChildren.filter(
            (child) => child !== widgetId,
          );

          this.widgetChanges[parentId] = {
            ...this.widgetChanges[parentId],
            children: newParentChildren,
          };
        }
      }

      // nothing else to do for deletions
      return;
    }

    let widgetIsNew = false;
    // Widget was created (either explicitly by AI or implicitly by adding a tab, section col, etc.)
    if (!existingWidget) {
      widgetIsNew = true;
      this.createNewWidgetInDataTree(widgetId);
      existingWidget = this.widgetChanges[widgetId] as Partial<WidgetProps>;
    } else {
      this.widgetChanges[widgetId] = {};
    }

    const propSections = getItemPropertyPaneConfig(
      existingWidget.type as ItemWithPropertiesType,
    );
    const allProperties = propSections
      .flatMap((section) => section.children)
      .filter(Boolean);

    // Get changed keys from our modified paths tracking
    this.widgetChangedKeys[widgetId] = this.widgetChangedKeys[widgetId] || [];

    this.discardedEdits[widgetId] = [];

    if (!widgetIsNew) {
      for (const [topLevelKey, changeSetObj] of Object.entries(
        this.nestedChangesByWidgetId?.[widgetId] ?? {},
      )) {
        const shouldCopyProperty =
          isArray(changeSetObj) || isPlainObject(changeSetObj);

        if (
          !this.widgetChanges?.[widgetId]?.[topLevelKey] &&
          shouldCopyProperty &&
          !this.widgetChangedKeys[widgetId].includes(topLevelKey)
        ) {
          set(
            this.widgetChanges,
            `${widgetId}.${topLevelKey}`,
            fastClone(existingWidget[topLevelKey as keyof WidgetProps]),
          );
        }
      }
    }

    // Copy our nested changes directly into dataTreeChanges
    if (this.nestedChangesByWidgetId[widgetId]) {
      this.widgetChanges[widgetId] = merge(
        this.widgetChanges[widgetId] ?? {},
        this.nestedChangesByWidgetId[widgetId],
      );
    }

    const temporaryDynamicProperties =
      getWidgetDynamicPropertyPathList(existingWidget);

    sanitizeEdits({
      changesByWidgetId: this.widgetChanges,
      properties: allProperties as PropertyPaneConfig[],
      discardedEdits: this.discardedEdits,
      dynamicProperties: temporaryDynamicProperties,
      previousWidget: existingWidget,
      theme: this.context.theme,
      featureFlags: this.context.featureFlags,
      changedKeys: this.widgetChangedKeys[widgetId],
      widgets: this.context.canvasWidgets,
    });

    set(
      this.widgetChanges,
      `${widgetId}.dynamicPropertyPathList`,
      uniqBy(temporaryDynamicProperties, (prop) => prop.key),
    );

    // update dynamic properties
    this.widgetChanges[widgetId] = mergeUpdatesWithBindingsOrTriggers(
      existingWidget,
      propSections,
      flattenObject(this.widgetChanges[widgetId] ?? {}),
      this.context.featureFlags[Flag.ENABLE_DEEP_BINDINGS_PATHS],
    );
    this.widgetChanges[widgetId] = unflattenObject(
      this.widgetChanges[widgetId] ?? {},
    );

    this.widgetChangedKeys[widgetId] = uniq(
      (this.widgetChangedKeys[widgetId] ?? []).filter((key) => {
        if (
          this.discardedEdits[widgetId].some(
            (edit) => edit.propertyName === key,
          )
        ) {
          return false;
        }
        return true;
      }),
    );

    // Ensure widgetName is unique
    const widgetName = this.widgetChanges[widgetId]?.widgetName as
      | string
      | undefined;
    if (
      widgetName &&
      this.widgetChanges[widgetId] &&
      this.allEntityNames.includes(widgetName)
    ) {
      const newWidgetName = getNextEntityName(widgetName, this.allEntityNames);
      (this.widgetChanges[widgetId] as Record<string, unknown>).widgetName =
        newWidgetName;
      this.allEntityNames.push(newWidgetName);
    }

    if (this.widgetHasNoChanges(widgetId)) {
      delete this.widgetChanges[widgetId];
      delete this.widgetChangedKeys[widgetId];
      return;
    }
  }

  private createApiChanges({
    apiId,
    apiName,
    blocks,
  }: {
    apiId: string;
    apiName: string;
    blocks: ApiDtoWithPb["apiPb"]["blocks"];
  }) {
    const apiMetadata = {
      id: apiId,
      name: apiName,
      organization: this.context.organizationId,
      application: this.context.applicationId,
      pageId: this.context.pageId,
      // add others: environment etc.?
    };
    this.apiChanges[apiId] = {
      id: apiId,
      name: apiName,
      applicationId: this.context.applicationId,
      organizationId: this.context.organizationId,
      // triggerType these seem unnecessary?
      triggerType: 0, // 0 === application
      apiPb: {
        metadata: apiMetadata,
        blocks: (blocks ?? []).map((step: any) => {
          return ClarkProcessor.createApiBlockFromClarkBlock(step);
        }),
        trigger: {
          application: {
            id: this.context.applicationId,
            pageId: this.context.pageId,
          },
        },
      },
    };

    return this.apiChanges[apiId];
  }

  // blocks getting updated here are in apiChanges, so
  // they're in the ApiDtoWithPb structure, not the structure of blocks in the clark actions
  // which need some massaging to get into the ApiDtoWithPb structure
  private findAndUpdateApiBlock(
    apiId: string,
    stepName: string,
    stepEdit: {
      property: string;
      value: unknown;
      action: "set";
    },
  ) {
    const getStepBodyKey = (
      obj: Record<string, unknown>,
    ): string | undefined => {
      for (const key in obj) {
        if (key !== "integration") return key;
      }
    };

    const blocks = this.apiChanges[apiId].apiPb.blocks ?? [];
    const path = ClarkProcessor.findStepPath(blocks, stepName);
    if (path) {
      const dottedPath = path.join(".");
      let fullPath = `${apiId}.apiPb.blocks.${dottedPath}`;
      const existingBlock: any = get(this.apiChanges, fullPath);
      const blockType = ClarkProcessor.getBlockType(existingBlock);
      const stepBodyKey = getStepBodyKey(existingBlock[blockType]);
      if (stepBodyKey) {
        fullPath += `.step.${stepBodyKey}.${stepEdit.property}`;
      } else {
        fullPath += `.${stepEdit.property}`;
      }
      if (!stepBodyKey) {
        throw new Error("No step body key found");
      }
      set(this.apiChanges, fullPath, stepEdit.value);
    }
  }

  static findStepPath(
    rootBlock: any,
    targetName: string,
    currentPath: Array<string | number> = [],
  ): Array<string | number> | null {
    if (rootBlock.name === targetName) {
      return currentPath;
    }

    // If we're looking at a top-level array of steps
    if (Array.isArray(rootBlock) || rootBlock.blocks) {
      const hasBlocksKey = rootBlock.blocks ? true : false;
      const blocksToLoop = hasBlocksKey ? rootBlock.blocks : rootBlock;

      for (let i = 0; i < blocksToLoop.length; i++) {
        const nestedPath = [
          ...currentPath,
          ...(hasBlocksKey ? ["blocks", i] : [i]),
        ];
        const result = ClarkProcessor.findStepPath(
          blocksToLoop[i],
          targetName,
          nestedPath,
        );
        if (result) return result;
      }
    }

    const blockType = ClarkProcessor.getBlockType(rootBlock);
    // If this is an integration block, it doesn't have nested blocks, just give up
    if (blockType === "step") {
      return null;
    } else {
      // Handle control blocks that can contain nested blocks
      const blockBody = rootBlock[blockType];

      switch (blockType) {
        case "parallel": {
          if (blockBody.static) {
            // Check static paths
            for (const path of Object.keys(blockBody.static.paths)) {
              const nestedPath = [
                ...currentPath,
                blockType,
                "static",
                "paths",
                path,
                "blocks",
                0,
              ];
              const result = ClarkProcessor.findStepPath(
                blockBody.static.paths[path].blocks[0],
                targetName,
                nestedPath,
              );
              if (result) return result;
            }
          } else if (blockBody.dynamic) {
            // Check dynamic blocks
            for (let i = 0; i < blockBody.dynamic.blocks.length; i++) {
              const nestedPath = [
                ...currentPath,
                blockType,
                "dynamic",
                "blocks",
                i,
              ];
              const result = ClarkProcessor.findStepPath(
                blockBody.dynamic.blocks[i],
                targetName,
                nestedPath,
              );
              if (result) return result;
            }
          }
          break;
        }
        case "conditional": {
          // Check if block
          if (blockBody.if) {
            const nestedPath = [...currentPath, blockType, "if"];
            const result = ClarkProcessor.findStepPath(
              blockBody.if,
              targetName,
              nestedPath,
            );
            if (result) return result;
          }

          // Check elseIf blocks
          if (blockBody.elseIf) {
            for (let i = 0; i < blockBody.elseIf.length; i++) {
              // Check blocks within each elseIf
              for (let j = 0; j < blockBody.elseIf[i].blocks.length; j++) {
                const nestedPath = [
                  ...currentPath,
                  blockType,
                  "elseIf",
                  i,
                  "blocks",
                  j,
                ];
                const result = ClarkProcessor.findStepPath(
                  blockBody.elseIf[i].blocks[j],
                  targetName,
                  nestedPath,
                );
                if (result) return result;
              }
            }
          }

          // Check else blocks
          if (blockBody.else) {
            for (let i = 0; i < blockBody.else.blocks.length; i++) {
              const nestedPath = [
                ...currentPath,
                blockType,
                "else",
                "blocks",
                i,
              ];
              const result = ClarkProcessor.findStepPath(
                blockBody.else.blocks[i],
                targetName,
                nestedPath,
              );
              if (result) return result;
            }
          }
          break;
        }
        case "loop": {
          // Check loop blocks
          for (let i = 0; i < blockBody.blocks.length; i++) {
            const nestedPath = [...currentPath, blockType, "blocks", i];
            const result = ClarkProcessor.findStepPath(
              blockBody.blocks[i],
              targetName,
              nestedPath,
            );
            if (result) return result;
          }
          break;
        }
        case "tryCatch": {
          // Check try blocks
          for (let i = 0; i < blockBody.try.blocks.length; i++) {
            const nestedPath = [...currentPath, blockType, "try", "blocks", i];
            const result = ClarkProcessor.findStepPath(
              blockBody.try.blocks[i],
              targetName,
              nestedPath,
            );
            if (result) return result;
          }

          // Check catch blocks
          for (let i = 0; i < blockBody.catch.blocks.length; i++) {
            const nestedPath = [
              ...currentPath,
              blockType,
              "catch",
              "blocks",
              i,
            ];
            const result = ClarkProcessor.findStepPath(
              blockBody.catch.blocks[i],
              targetName,
              nestedPath,
            );
            if (result) return result;
          }

          // Check finally blocks
          for (let i = 0; i < blockBody.finally.blocks.length; i++) {
            const nestedPath = [
              ...currentPath,
              blockType,
              "finally",
              "blocks",
              i,
            ];
            const result = ClarkProcessor.findStepPath(
              blockBody.finally.blocks[i],
              targetName,
              nestedPath,
            );
            if (result) return result;
          }
          break;
        }
        case "stream": {
          // Check stream process blocks
          for (let i = 0; i < blockBody.process.blocks.length; i++) {
            const nestedPath = [
              ...currentPath,
              blockType,
              "process",
              "blocks",
              i,
            ];
            const result = ClarkProcessor.findStepPath(
              blockBody.process.blocks[i],
              targetName,
              nestedPath,
            );
            if (result) return result;
          }
          break;
        }
      }
    }

    return null;
  }

  static getBlockType(block: any) {
    if (block.conditional) {
      return "conditional";
    } else if (block.loop) {
      return "loop";
    } else if (block.tryCatch) {
      return "tryCatch";
    } else if (block.stream) {
      return "stream";
    } else if (block.parallel) {
      return "parallel";
    } else if (block.variables) {
      return "variables";
    } else if (block.break) {
      return "break";
    } else if (block.return) {
      return "return";
    } else if (block.integration || block.step) {
      // clark returns "integration" instead of "step" for step blocks
      return "step";
    } else {
      throw new Error(`Unknown block type for block ${JSON.stringify(block)}`);
    }
  }

  // the block structure is different from clark than the exact apiPb format
  // so we need to massage the block structure to match the apiPb format
  static createApiBlockFromClarkBlock(
    clarkApiBlock: ClarkApiBlockSpec,
  ): BaseBlock {
    const blockType = ClarkProcessor.getBlockType(clarkApiBlock.block);

    if (blockType === "step") {
      const block = clarkApiBlock.block as ClarkApiStepBlockSpec;
      return {
        name: clarkApiBlock.name,
        step: {
          integration: block.integration?.name?.includes("__")
            ? (block.integration?.name as string).split("__")[1]
            : block.integration?.name,
          [block.integration?.type as string]: {},
        },
      };
    } else {
      // this means it's a control block
      // they have a different shape than standard blocks
      // there's no step object, the control block name is the property object key,
      // and some can have children
      // ex:
      // {
      //   name: 'Parallel1',
      //   parallel: { ...configHere }
      // }

      const block = clarkApiBlock.block as Record<string, unknown>;
      const blockBody = fastClone(block[blockType]);

      // TODO: Unpack nested blocks inside the control blocks here

      const apiBlock = {
        name: clarkApiBlock.name,
        [blockType]: {
          ...(blockBody as Record<string, unknown>),
        },
      };

      switch (blockType) {
        case "parallel": {
          const parallelBlockBody = blockBody as ParallelConfig;

          // set default wait
          if (!parallelBlockBody.wait) {
            set(apiBlock, "parallel.wait", "WAIT_ALL");
          }

          if (parallelBlockBody.static) {
            const apiPbStaticPaths: ParallelConfig["static"] = { paths: {} };

            (parallelBlockBody.static.paths as unknown as any[]).forEach(
              (path: { name: string; blocks: ClarkApiBlockSpec[] }) => {
                apiPbStaticPaths.paths[path.name] = {
                  blocks: path.blocks.map((block: any) =>
                    ClarkProcessor.createApiBlockFromClarkBlock(block),
                  ),
                };
              },
            );

            (apiBlock.parallel as unknown as any).static.paths =
              apiPbStaticPaths.paths;
          } else if (parallelBlockBody.dynamic) {
            parallelBlockBody.dynamic = {
              ...parallelBlockBody.dynamic,
              variables: {
                item: "item",
                ...((parallelBlockBody.dynamic.variables as any) ?? {}), // variables might be undefined by clark
              },
              paths: parallelBlockBody.dynamic.paths,
              blocks: (parallelBlockBody.dynamic?.blocks ?? []).map(
                (block: any) =>
                  ClarkProcessor.createApiBlockFromClarkBlock(block),
              ),
            };
            (apiBlock.parallel as any).dynamic = parallelBlockBody.dynamic;
          }
          break;
        }
        case "conditional": {
          const conditionalBlockBody = blockBody as ConditionalConfig;

          if (conditionalBlockBody.if) {
            set(
              apiBlock,
              "conditional.if.blocks",
              conditionalBlockBody.if.blocks?.map((block: any) =>
                ClarkProcessor.createApiBlockFromClarkBlock(block),
              ),
            );
          } else if (
            conditionalBlockBody.elseIf &&
            conditionalBlockBody.elseIf.length > 0
          ) {
            conditionalBlockBody.elseIf.forEach((condition) => {
              condition.blocks = condition.blocks?.map((block: any) =>
                ClarkProcessor.createApiBlockFromClarkBlock(block),
              );
            });
          } else if (conditionalBlockBody.else) {
            set(
              apiBlock,
              "conditional.else.blocks",
              conditionalBlockBody.else.blocks?.map((block: any) =>
                ClarkProcessor.createApiBlockFromClarkBlock(block),
              ),
            );
          }
          break;
        }
        case "loop": {
          const loopBlockBody = blockBody as LoopConfig;

          if (!get(apiBlock, "loop.variables.item")) {
            set(apiBlock, "loop.variables.item", "item");
          }
          if (!get(apiBlock, "loop.variables.index")) {
            set(apiBlock, "loop.variables.index", "index");
          }

          set(
            apiBlock,
            "loop.blocks",
            loopBlockBody.blocks?.map((block: any) =>
              ClarkProcessor.createApiBlockFromClarkBlock(block),
            ),
          );
          break;
        }
        case "tryCatch": {
          const tryCatchBlockBody = blockBody as TryCatchConfig;

          // add the error variable to the try catch
          if (!get(apiBlock, "tryCatch.variables.error")) {
            set(apiBlock, "tryCatch.variables.error", "error");
          }

          set(
            apiBlock,
            "tryCatch.try.blocks",
            tryCatchBlockBody.try?.blocks?.map((block: any) =>
              ClarkProcessor.createApiBlockFromClarkBlock(block),
            ),
          );

          set(
            apiBlock,
            "tryCatch.catch.blocks",
            tryCatchBlockBody.catch?.blocks?.map((block: any) =>
              ClarkProcessor.createApiBlockFromClarkBlock(block),
            ),
          );

          set(
            apiBlock,
            "tryCatch.finally.blocks",
            tryCatchBlockBody.finally?.blocks?.map((block: any) =>
              ClarkProcessor.createApiBlockFromClarkBlock(block),
            ),
          );
          break;
        }
        case "variables": {
          const variablesBlockBody = blockBody as VariablesConfig;

          set(
            apiBlock,
            "variables.items",
            variablesBlockBody.items.map((clarkVariable: any) => ({
              ...clarkVariable,
              type: VariableType.SIMPLE,
              mode: VariableMode.READWRITE,
            })),
          );

          break;
        }
        case "stream": {
          const streamBlockBody = blockBody as StreamConfig;

          set(
            apiBlock,
            "stream.process.blocks",
            streamBlockBody.process?.blocks?.map((block: any) =>
              ClarkProcessor.createApiBlockFromClarkBlock(block),
            ),
          );
          break;
        }
      }

      return apiBlock;
    }
  }

  public hasResults() {
    const hasStateVarChanges =
      Object.keys(this.stateVariableChanges).length > 0;
    const hasTimerChanges = Object.keys(this.timerChanges).length > 0;
    const hasEventChanges = Object.keys(this.eventChanges).length > 0;
    const hasApiChanges = Object.keys(this.apiChanges).length > 0;
    const hasWidgetChanges = Object.keys(this.widgetChanges).length > 0;
    const hasRenameChanges = Object.keys(this.renamesById).length > 0;
    const hasWidgetMoves = Object.keys(this.widgetMovesById).length > 0;
    return (
      hasStateVarChanges ||
      hasTimerChanges ||
      hasEventChanges ||
      hasApiChanges ||
      hasWidgetChanges ||
      hasRenameChanges ||
      hasWidgetMoves
    );
  }

  /* utilities */
  /**
   * Sets a change for a widget property using the nested structure.
   *
   * @param widgetId - ID of the widget to modify
   * @param propertyPath - Path to the property, can be nested (e.g. "primaryColumns.column1.width")
   * @param value - Value to set at the specified path
   */
  private setNestedChangeForWidget(
    widgetId: string,
    propertyPath: string,
    value: any,
  ) {
    // Ensure widget exists in our changes map
    if (!this.nestedChangesByWidgetId[widgetId]) {
      this.nestedChangesByWidgetId[widgetId] = {};
    }

    // Track that this specific path was modified
    if (!this.widgetChangedKeys[widgetId]) {
      this.widgetChangedKeys[widgetId] = [];
    }
    this.widgetChangedKeys[widgetId].push(propertyPath);

    // Handle objects specially by cloning to prevent reference issues
    let valueToUse = value;
    if (isPlainObject(value) || isArray(value)) {
      valueToUse = fastClone(value);
    }

    // Use lodash set to handle nested paths
    set(
      this.nestedChangesByWidgetId[widgetId] as Record<string, unknown>,
      propertyPath,
      valueToUse,
    );
  }

  private setChangedKeysForNewWidget(widgetId: string) {
    this.widgetChangedKeys[widgetId] = ["widgetId", "widgetName", "type"];
  }

  private widgetHasNoChanges(widgetId: string) {
    return (
      this.widgetChangedKeys[widgetId]?.filter(
        (key) =>
          ![
            "dynamicPropertyPathList",
            "dynamicBindingPathList",
            "dynamicTriggerPathList",
          ].includes(key),
      ).length === 0
    );
  }

  private getPropertyName(
    action: ClarkComponentAction,
    widgetType: undefined | WidgetProps["type"],
  ) {
    if ("property" in action) {
      if (
        widgetType === WidgetTypes.BUTTON_WIDGET &&
        action.property === "textProps.textStyle.textColor.default"
      ) {
        return "textColor";
      }
      return action.property ?? "";
    }
    return "";
  }

  private getItems(widget: Partial<WidgetProps>) {
    switch (widget.type) {
      case WidgetTypes.MENU_WIDGET:
        return {
          items: (widget as any).manualChildren,
          itemCount: (widget as any).manualChildren.length,
        };
      case WidgetTypes.TABLE_WIDGET:
        // FIXME: once we get actin hooks working this ?? {} isn't required
        return {
          items: Object.keys((widget as any)?.primaryColumns ?? {}),
          itemCount: Object.keys((widget as any)?.primaryColumns ?? {}).length,
        };
      case WidgetTypes.TABS_WIDGET:
        return {
          items: (widget as any).tabs,
          itemCount: (widget as any).tabs.length,
        };
      case WidgetTypes.SECTION_WIDGET:
        return {
          items: (widget as any).children,
          itemCount: (widget as any).children.length,
        };
      case WidgetTypes.KEY_VALUE_WIDGET:
        return {
          items: Object.keys((widget as any)?.properties ?? {}),
          itemCount: Object.keys((widget as any)?.properties ?? {}).length,
        };
      default:
        return {
          items: [],
          itemCount: 0,
        };
    }
  }

  private getItemIdAndExistence({
    action,
    widget,
    metadata,
    items,
    changes,
  }: {
    action: ClarkComponentAction;
    widget: Partial<WidgetProps>;
    metadata: EditMetadata | undefined;
    items: Array<any>;
    changes: Record<string, unknown>;
  }): ItemIdAndExistence {
    switch (widget.type) {
      case WidgetTypes.MENU_WIDGET:
        if ("menu_item" in action && action.menu_item != null) {
          const menuItemIndex = parseInt(action.menu_item, 10);
          const existingIndices = Object.values(
            (metadata as EditMetadata).idsToIndices,
          );
          const menuItemExists =
            menuItemIndex != null &&
            metadata &&
            existingIndices.includes(menuItemIndex);
          return {
            itemId: menuItemIndex,
            exists: Boolean(menuItemExists),
            type: "menu_item",
          };
        }
        break;
      case WidgetTypes.TABLE_WIDGET:
        if ("table_column" in action && action.table_column != null) {
          const primaryColumns = ((widget as any).primaryColumns ??
            {}) as Record<string, any>;

          const column =
            getColumnIdFromAi(action.table_column, primaryColumns) ??
            action.table_column;

          const columnExists =
            column &&
            (items.includes(column) ||
              (changes.columnOrder as string[])?.includes(column));

          return {
            itemId: column,
            exists: Boolean(columnExists),
            type: "column",
          };
        }
        break;
      case WidgetTypes.TABS_WIDGET:
        if ("tabs_tab" in action && action.tabs_tab) {
          const tabIndex = parseInt(action.tabs_tab, 10);
          const existingIndices = Object.values(
            (metadata as EditMetadata).idsToIndices,
          );
          const tabExists =
            tabIndex != null && metadata && existingIndices.includes(tabIndex);
          return {
            itemId: tabIndex,
            exists: Boolean(tabExists),
            type: "tab",
          };
        }
        break;
      case WidgetTypes.KEY_VALUE_WIDGET:
        if (
          "key_value_property" in action &&
          action.key_value_property != null
        ) {
          const kvItem = getKvPropertyIdFromAi(
            action.key_value_property,
            (widget as any).properties,
          );
          const kvItemExists =
            (widget as any).properties[kvItem] != null ||
            changes[`properties.${kvItem}`] != null;
          return {
            itemId: kvItem,
            exists: Boolean(kvItemExists),
            type: "key_value_property",
          };
        }
        break;
      case WidgetTypes.SECTION_WIDGET:
        if ("section_column" in action && action.section_column != null) {
          const sectionColumnIndex = parseInt(action.section_column, 10);
          const existingIndices = Object.values(
            (metadata as EditMetadata).idsToIndices,
          );
          const columnExists =
            sectionColumnIndex != null &&
            metadata &&
            existingIndices.includes(sectionColumnIndex);
          return {
            itemId: sectionColumnIndex,
            exists: Boolean(columnExists),
            type: "section_column",
          };
        }
        break;
    }
    return {
      itemId: undefined,
      exists: false,
      type: undefined,
    };
  }

  private handleTabActions({
    action,
    itemId,
    itemExists,
    canvasWidget,
    widgetId,
  }: ItemHandlerProps<
    SetTabAction | RemoveTabAction | RemoveTabPropertyAction
  >) {
    const metadata = this.metadataByWidgetId[widgetId];
    if (metadata == null) {
      throw new Error(
        `Metadata for widget ${widgetId} not found in ClarkProcessor`,
      );
    }
    const itemIndex =
      typeof itemId === "string" ? parseInt(itemId, 10) : itemId;
    const idsToIndices = metadata.idsToIndices;
    const indicesToIds = Object.fromEntries(
      Object.entries(idsToIndices).map(([id, index]) => [index, id]),
    );
    const tabId = indicesToIds[itemIndex];

    switch (action.action) {
      case "set":
        if (itemExists && tabId != null) {
          updateTab({
            tabId,
            property: action.property as string,
            value: action.value,
            widget: canvasWidget,
            changesByWidgetId: this.nestedChangesByWidgetId,
            changedKeysByWidgetId: this.widgetChangedKeys ?? {},
            updateComponentFn: this.updateComponentFn,
          });
        } else if (!itemExists) {
          const newWidgetId = addTab({
            property: action.property as string,
            value: action.value,
            widget: canvasWidget,
            changesByWidgetId: this.nestedChangesByWidgetId,
            changedKeysByWidgetId: this.widgetChangedKeys ?? {},
            createComponentFn: this.createComponentFn,
          });
          set(
            this.metadataByWidgetId,
            `${widgetId}.idsToIndices.${newWidgetId}`,
            itemIndex,
          );
        }
        break;
      case "remove":
        if (itemExists && "property" in action) {
          updateTab({
            tabId,
            property: action.property as string,
            value: undefined,
            changesByWidgetId: this.nestedChangesByWidgetId,
            widget: canvasWidget,
            changedKeysByWidgetId: this.widgetChangedKeys ?? {},
            updateComponentFn: this.updateComponentFn,
          });
        } else if (itemExists) {
          removeTab({
            tabId,
            widget: canvasWidget,
            changesByWidgetId: this.nestedChangesByWidgetId,
            changedKeysByWidgetId: this.widgetChangedKeys ?? {},
            deleteComponentFn: this.deleteComponentFn,
          });
        }
        break;
    }
  }

  private handleSectionColumnActions({
    action,
    itemId,
    itemExists,
    canvasWidget,
    widgetId,
  }: ItemHandlerProps<SectionColumnAction>) {
    const metadata = this.metadataByWidgetId[widgetId];
    if (metadata == null) {
      throw new Error(
        `Metadata for widget ${widgetId} not found in ClarkProcessor`,
      );
    }
    const itemIndex =
      typeof itemId === "string" ? parseInt(itemId, 10) : itemId;
    const idsToIndices = metadata.idsToIndices;
    const indicesToIds = Object.fromEntries(
      Object.entries(idsToIndices).map(([id, index]) => [index, id]),
    );
    const sectionColumnId = indicesToIds[itemIndex];

    switch (action.action) {
      case "set":
        if (itemExists && sectionColumnId != null) {
          updateSectionColumn({
            sectionColumnId,
            property: action.property as string,
            value: action.value,
            widget: canvasWidget,
            changesByWidgetId: this.nestedChangesByWidgetId,
            changedKeysByWidgetId: this.widgetChangedKeys ?? {},
          });
        } else if (!itemExists) {
          const newWidgetId = addSectionColumn({
            property: action.property as string,
            value: action.value,
            widget: canvasWidget,
            changedKeysByWidgetId: this.widgetChangedKeys ?? {},
            addSectionColumnFn: this.addSectionColumnFn,
          });
          set(
            this.metadataByWidgetId,
            `${widgetId}.idsToIndices.${newWidgetId}`,
            itemIndex,
          );
        }
        break;
      case "remove":
        if (itemExists) {
          removeSectionColumn({
            sectionColumnId,
            widget: canvasWidget,
            changesByWidgetId: this.nestedChangesByWidgetId,
            changedKeysByWidgetId: this.widgetChangedKeys ?? {},
            deleteComponentFn: this.deleteComponentFn,
          });
        }
        break;
    }
  }

  private handleColumnActions({
    action,
    itemId,
    itemExists,
    canvasWidget,
    widgetId,
    itemCount,
  }: ItemHandlerProps<ColumnAction>) {
    const changes = this.nestedChangesByWidgetId[widgetId];
    if (changes == null) {
      throw new Error(
        `Changes for widget ${widgetId} not found in ClarkProcessor`,
      );
    }

    // Always track the widgetId in the changedKeys when modifying table columns
    if (!this.widgetChangedKeys[widgetId]) {
      this.widgetChangedKeys[widgetId] = [];
    }

    const tableCanvasWidget = canvasWidget as TableWidgetProps;

    switch (action.action) {
      case "set":
        if (!itemExists) {
          addColumns({
            widget: tableCanvasWidget,
            changes,
            numColumns: itemCount,
            columnName: String(itemId),
            changedKeys: this.widgetChangedKeys[widgetId],
          });
        }
        if (action.property) {
          updateColumns({
            action: action as SetColumnAction,
            widget: tableCanvasWidget,
            changes,
            changedKeys: this.widgetChangedKeys[widgetId],
            dataTree: this.context.dataTree,
            routes: this.context.routes,
            columnName: String(itemId),
          });
        }
        break;
      case "add":
        if (itemExists) {
          updateColumns({
            action: action as AddColumnEventAction,
            widget: tableCanvasWidget,
            changes,
            dataTree: this.context.dataTree,
            routes: this.context.routes,
            columnName: String(itemId),
            changedKeys: this.widgetChangedKeys[widgetId],
          });
        }
        break;
      case "remove":
        if (itemExists && "property" in action) {
          updateColumns({
            action: action as RemoveColumnEventAction,
            widget: tableCanvasWidget,
            changes,
            dataTree: this.context.dataTree,
            routes: this.context.routes,
            columnName: String(itemId),
            changedKeys: this.widgetChangedKeys[widgetId],
          });

          // Record property removal
          this.widgetChangedKeys[widgetId].push(
            `primaryColumns.${String(itemId)}.${action.property}`,
          );
          if (tableCanvasWidget.derivedColumns?.[String(itemId)]) {
            this.widgetChangedKeys[widgetId].push(
              `derivedColumns.${String(itemId)}.${action.property}`,
            );
          }
        } else if (itemExists) {
          removeColumns({
            widget: tableCanvasWidget,
            changes,
            columnName: String(itemId),
            changedKeys: this.widgetChangedKeys[widgetId],
            deleteComponentPropertiesFn: this.deleteComponentPropertiesFn,
          });

          // Record column removal
          this.widgetChangedKeys[widgetId].push(
            `primaryColumns.${String(itemId)}`,
          );
          if (tableCanvasWidget.derivedColumns?.[String(itemId)]) {
            this.widgetChangedKeys[widgetId].push(
              `derivedColumns.${String(itemId)}`,
            );
          }
        }
        break;
    }
  }

  private handleMenuItemActions({
    action,
    itemId,
    itemExists,
    widgetId,
    canvasWidget,
  }: ItemHandlerProps<MenuItemAction>) {
    const metadata = this.metadataByWidgetId[widgetId];
    if (metadata == null) {
      throw new Error(
        `Metadata for widget ${widgetId} not found in ClarkProcessor`,
      );
    }
    if (typeof itemId === "string") {
      itemId = parseInt(itemId, 10);
    }
    if (!this.widgetChangedKeys[widgetId]) {
      this.widgetChangedKeys[widgetId] = [];
    }
    const itemIndex =
      typeof itemId === "string" ? parseInt(itemId, 10) : itemId;
    const idsToIndices = metadata.idsToIndices;
    const indicesToIds = Object.fromEntries(
      Object.entries(idsToIndices).map(([id, index]) => [index, id]),
    );
    const menuItemId = indicesToIds[itemIndex];
    switch (action.action) {
      case "set":
        if (!itemExists) {
          const newMenuItemId = addMenuItem({
            property: action.property as string,
            value: action.value,
            widget: canvasWidget,
            changesByWidgetId: this.nestedChangesByWidgetId,
            changedKeys: this.widgetChangedKeys ?? {},
          });
          set(
            this.metadataByWidgetId,
            `${widgetId}.idsToIndices.${newMenuItemId}`,
            itemId,
          );
        } else {
          updateMenuItem({
            menuItemId,
            property: action.property as string,
            value: action.value,
            widget: canvasWidget,
            changedKeys: this.widgetChangedKeys ?? {},
            changesByWidgetId: this.nestedChangesByWidgetId,
          });
        }
        break;
      case "remove": {
        if (itemExists && "property" in action) {
          updateMenuItem({
            menuItemId,
            property: action.property as string,
            value: undefined,
            widget: canvasWidget,
            changedKeys: this.widgetChangedKeys ?? {},
            changesByWidgetId: this.nestedChangesByWidgetId,
          });
        } else if (itemExists) {
          removeMenuItem({
            widget: canvasWidget,
            menuItemId,
            changesByWidgetId: this.nestedChangesByWidgetId,
            changedKeys: this.widgetChangedKeys ?? {},
          });
        }
        break;
      }
      case "reset": {
        if (itemExists && action.property) {
          updateMenuItem({
            menuItemId,
            property: action.property as string,
            value: undefined,
            widget: canvasWidget,
            changedKeys: this.widgetChangedKeys ?? {},
            changesByWidgetId: this.nestedChangesByWidgetId,
          });
        }
      }
    }
  }

  private handleKvActions({
    action,
    itemId,
    itemExists,
    canvasWidget,
    widgetId,
  }: ItemHandlerProps<KvAction>) {
    const changes = this.nestedChangesByWidgetId[widgetId];
    if (changes == null) {
      throw new Error(
        `Changes for widget ${widgetId} not found in ClarkProcessor`,
      );
    }
    if (!this.widgetChangedKeys[widgetId]) {
      this.widgetChangedKeys[widgetId] = [];
    }
    if (!itemExists) {
      addKvItem({
        action: action,
        widget: canvasWidget,
        changes,
        dataTree: this.context.dataTree,
        routes: this.context.routes,
        kvProperty: String(itemId),
        updateComponentFn: this.updateComponentFn,
        changedKeys: this.widgetChangedKeys ?? {},
      });
    } else if (itemExists && action.action === "remove" && !action.property) {
      removeKvItem({
        widget: canvasWidget,
        changes,
        kvProperty: String(itemId),
        deleteComponentPropertiesFn: this.deleteComponentPropertiesFn,
        changedKeys: this.widgetChangedKeys ?? {},
      });
    } else {
      updateKvItem({
        action: action,
        widget: canvasWidget,
        changes,
        dataTree: this.context.dataTree,
        routes: this.context.routes,
        kvProperty: String(itemId),
        updateComponentFn: this.updateComponentFn,
        changedKeys: this.widgetChangedKeys ?? {},
      });
    }
  }

  private createNewWidgetInDataTree(widgetId: string) {
    const type: WidgetTypes | undefined = this.nestedChangesByWidgetId[widgetId]
      ?.type as WidgetTypes | undefined;
    const parentId = this.nestedChangesByWidgetId[widgetId]?.parentId;

    if (!type) {
      throw new Error(
        `Widget with id ${widgetId} has no type and was not found in canvasWidgets`,
      );
    }

    if (!parentId) {
      throw new Error(
        `Widget with id ${widgetId} has no parentId and was not found in canvasWidgets`,
      );
    }

    const newWidget = fastClone(this.nestedChangesByWidgetId[widgetId]);
    this.widgetChanges[widgetId] = newWidget;
  }

  private getInitialWidgetProps(widgetType: WidgetTypes, parentId: string) {
    const baseProps = {
      ...WidgetConfigResponse.getCreateConfig(
        widgetType,
        this.context.featureFlags,
        this.context.theme,
      ),
      left: Dimension.gridUnit(0),
      top: Dimension.gridUnit(0),
    };

    // get the existing parent widget
    const parentWidget = this.context.canvasWidgets[parentId];
    const parentLayout = parentWidget?.layout;

    // Naive positioning for fixed grid
    // todo: eric - ai team - improve this
    if (parentLayout === CanvasLayout.FIXED || !parentLayout) {
      // find parent widget child with highest top + height value
      const children = parentWidget.children || [];
      let maxBottom = 0;

      children.forEach((childId) => {
        const child = this.context.canvasWidgets[childId];
        if (child) {
          const childTop = (child.top as Dimension).value;
          const childHeight = (child.height as Dimension).value;
          const bottom = childTop + childHeight;
          maxBottom = Math.max(maxBottom, bottom);
        }
      });

      // now find parent widget child with highest top + height value
      // from any new possible children that were created by Clark
      const newChildren = (this.nestedChangesByWidgetId[parentId]?.children ||
        []) as string[];
      newChildren.forEach((childId) => {
        const child =
          this.context.canvasWidgets[childId] ??
          this.nestedChangesByWidgetId[childId];
        if (child) {
          const childTop = (child.top as Dimension).value;
          const childHeight = (child.height as Dimension).value;
          const bottom = childTop + childHeight;
          maxBottom = Math.max(maxBottom, bottom);
        }
      });

      baseProps.top = Dimension.gridUnit(maxBottom === 0 ? 1 : maxBottom + 1);
      baseProps.left = Dimension.gridUnit(0);
    }

    if (widgetType === WidgetTypes.MENU_WIDGET) {
      (baseProps as any).manualChildren = [];
    }

    return baseProps;
  }

  private createDefaultWidgetAndChildWidgets(
    widgetId: string,
    type: WidgetTypes,
    widgetName: string,
    parentId: string,
  ) {
    const parent = this.context.canvasWidgets[parentId] as FlattenedWidgetProps;

    if (!parent) {
      throw new Error(
        `Parent widget with id ${parentId} not found in canvasWidgets`,
      );
    }

    const initialNewWidgetProps = fastClone(
      this.getInitialWidgetProps(type, parentId),
    );
    const newWidget = {
      ...initialNewWidgetProps,
      widgetName,
      widgetId,
      parentId: parent.widgetId,
      type,
    };

    if (parent.layout === CanvasLayout.FIXED || !parent.layout) {
      if (
        (parent.width.mode === "gridUnit" || parent.width.mode === "px") &&
        parent.width.value > 0
      ) {
        newWidget.width = parent.width;
      }
      // insert at the bottom of the parents children
      // we need to merge canvasWidget from context with the nested changes, in case multiple widget are created within a processing interval
      const allWidgetIds = uniq(
        Object.keys(this.context.canvasWidgets).concat(
          Object.keys(this.nestedChangesByWidgetId),
        ),
      );
      const mergedCanvasWidgets = allWidgetIds.reduce(
        (acc, widgetId) => {
          acc[widgetId] = {
            ...this.context.canvasWidgets[widgetId],
            ...this.nestedChangesByWidgetId[widgetId],
          };
          return acc;
        },
        {} as Record<string, Partial<WidgetProps>>,
      );
      const updatedPosition = calculateNewWidgetPosition({
        widget: newWidget,
        parentId,
        canvasWidgets: mergedCanvasWidgets as CanvasWidgetsReduxState,
      });
      newWidget.left = updatedPosition.left;
      newWidget.top = updatedPosition.top;
    }
    this.nestedChangesByWidgetId[widgetId] = newWidget;

    this.setChangedKeysForNewWidget(widgetId);
  }

  static getClarkApiBlockPaths(clarkBlock: Record<string, unknown>) {
    const paths: string[] = [];
    const skippedProperties = ["blocks", "shape"];

    const traverse = (
      obj: Record<string, unknown>,
      currentPath: string = "",
    ) => {
      for (const [key, value] of Object.entries(obj)) {
        // Skip the 'blocks' property to avoid traversing into nested blocks
        if (skippedProperties.includes(key)) {
          continue;
        }

        const newPath = currentPath ? `${currentPath}.${key}` : key;

        // If the value is an object and not null, recursively traverse it
        if (value && typeof value === "object" && !Array.isArray(value)) {
          traverse(value as Record<string, unknown>, newPath);
        } else {
          // Add path for leaf nodes (non-object values)
          paths.push(newPath);
        }
      }
    };

    traverse(clarkBlock);
    return paths;
  }

  private getGridPosition({
    parentWidget,
    oldParentWidget,
    relativeWidget,
    widget,
    position,
  }: {
    parentWidget: Partial<WidgetProps>;
    oldParentWidget: Partial<WidgetProps>;
    relativeWidget: Partial<WidgetProps>;
    widget: Partial<WidgetProps>;
    position: "left" | "right" | "above" | "below" | "inside";
  }) {
    if (
      !parentWidget.left ||
      !parentWidget.top ||
      !parentWidget.height ||
      !parentWidget.widgetId
    ) {
      return;
    }
    const colWidth =
      parentWidget.parentColumnSpace ?? GridDefaults.DEFAULT_GRID_COLUMN_WIDTH;
    const rowHeight =
      parentWidget.parentRowSpace ?? GridDefaults.DEFAULT_GRID_ROW_HEIGHT;
    const targetedSpot: {
      left: number;
      top: number;
    } = {
      left: relativeWidget.left?.value ?? 0,
      top: relativeWidget.top?.value ?? 0,
    };
    if (position === "left") {
      const adjustment =
        Dimension.toGridUnit(
          widget.width,
          oldParentWidget.parentColumnSpace ??
            GridDefaults.DEFAULT_GRID_COLUMN_WIDTH,
        ).raw()?.value ?? 0;
      targetedSpot.left -= adjustment;
    } else if (position === "right") {
      const adjustment =
        Dimension.toGridUnit(relativeWidget.width, colWidth).raw()?.value ?? 0;
      targetedSpot.left += adjustment;
    } else if (position === "above") {
      const adjustment =
        Dimension.toGridUnit(
          widget.height,
          oldParentWidget.parentRowSpace ??
            GridDefaults.DEFAULT_GRID_ROW_HEIGHT,
        ).raw()?.value ?? 0;
      targetedSpot.top -= adjustment;
    } else if (position === "below") {
      const adjustment =
        Dimension.toGridUnit(relativeWidget.height, rowHeight).raw()?.value ??
        0;
      targetedSpot.top += adjustment;
    }
    targetedSpot.left = Math.max(targetedSpot.left, 0);
    targetedSpot.top = Math.max(targetedSpot.top, 0);

    const occupiedSpaces = getOccupiedSpacesSelectorForContainer(
      parentWidget.widgetId,
    )(this.getState());

    const getShrinkDirection = () => {
      if (position === "left" || position === "right") {
        return position;
      }
      if (position === "above") {
        return "top";
      }
      if (position === "below") {
        return "bottom";
      }
    };
    const shrinkDirection = getShrinkDirection();

    const { position: newPosition, additionalShrinks } =
      calculateNewWidgetPositionOnMousePosition({
        widget: widget as any,
        canvasWidgets: this.context.canvasWidgets as CanvasWidgetsReduxState,
        // mouse position and dropTarget will be ignored in favor of desiredGridPosition
        mousePosition: {
          x: 0,
          y: 0,
        },
        dropTarget: {
          offset: { x: 0, y: 0 },
          size: { height: 0, width: 0 },
        },
        canvasScaleFactor: 1,
        occupiedSpaces,
        parent: parentWidget as FlattenedWidgetLayoutProps,
        desiredGridPosition: {
          left: targetedSpot.left,
          top: targetedSpot.top,
        },
        canShrink: true,
        additonalShrinkableWidgetIds:
          relativeWidget.widgetId && shrinkDirection
            ? {
                [relativeWidget.widgetId]: shrinkDirection,
              }
            : undefined,
      });

    const distance = (
      a: { left: number; top: number },
      b: { left: number; top: number },
    ) => {
      return Math.sqrt((a.left - b.left) ** 2 + (a.top - b.top) ** 2);
    };

    // check if new position is further from desired position than the current position
    // if so, use the current position
    if (
      widget.left != null &&
      widget.top != null &&
      distance(
        {
          left: newPosition.left.value,
          top: newPosition.top.value,
        },
        targetedSpot,
      ) >
        distance(
          {
            left: widget.left?.value ?? 0,
            top: widget.top?.value ?? 0,
          },
          targetedSpot,
        )
    ) {
      newPosition.left = widget.left;
      newPosition.top = widget.top;
      newPosition.height = widget.height ?? newPosition.height;
      newPosition.width = widget.width ?? newPosition.width;
    }

    if (
      // parent widget can have width 0 in the first column of the first section
      parentWidget?.width?.value !== 0 &&
      newPosition.left.value >= (parentWidget?.width?.value ?? 0)
    ) {
      // happens sometimes when there are a lot of full width children in the parent
      // and the new widget is being placed to the right of the last child
      return;
    }

    if (
      newPosition.left.value + (widget.width?.value ?? 0) >=
      parentWidget.left.value + (parentWidget?.width?.value ?? 0)
    ) {
      if (widget.width?.value && widget.width.value > 2) {
        // subtract to make it fit
        const overflow =
          newPosition.left.value +
          (widget.width?.value ?? 0) -
          (parentWidget.left.value + (parentWidget?.width?.value ?? 0));
        const newWidth = widget.width.value - overflow;
        this.resizeComponentFn({
          widgetId: widget.widgetId ?? "",
          size: {
            width: Dimension.gridUnit(newWidth),
            height: widget.height ?? Dimension.gridUnit(0),
          },
          position: {
            left: widget.left ?? Dimension.gridUnit(0),
            top: widget.top ?? Dimension.gridUnit(0),
          },
        });
      }
    }
    if (additionalShrinks) {
      // if there are any additional shrinkages, we need to update the height and width of that component
      for (const [widgetId, shrink] of Object.entries(additionalShrinks)) {
        const previousWidget = this.context.canvasWidgets[widgetId];
        // when moving left/right, only change width + left
        // when moving top/bottom, only change height + top
        if (position === "left" || position === "right") {
          this.resizeComponentFn({
            widgetId,
            size: {
              width: Dimension.gridUnit(shrink.width),
              height:
                previousWidget.height ?? Dimension.gridUnit(shrink.height),
            },
            position: {
              left: Dimension.gridUnit(shrink.left),
              top: previousWidget.top ?? Dimension.gridUnit(shrink.top),
            },
          });
        } else {
          this.resizeComponentFn({
            widgetId,
            size: {
              height: Dimension.gridUnit(shrink.height),
              width: previousWidget.width ?? Dimension.gridUnit(shrink.width),
            },
            position: {
              left: previousWidget.left ?? Dimension.gridUnit(shrink.left),
              top: Dimension.gridUnit(shrink.top),
            },
          });
        }
      }
    }

    return newPosition;
  }

  private moveWidgetToNewPosition(
    widgetId: string,
    position: "above" | "below" | "inside" | "left" | "right",
    relativeToWidgetId: string,
  ) {
    const widget = this.context.canvasWidgets[widgetId];
    const relativeWidget = this.context.canvasWidgets[relativeToWidgetId];
    if (
      !widget ||
      !widget.parentId ||
      !relativeWidget ||
      !relativeWidget.parentId
    ) {
      return;
    }
    const oldParentWidget = this.context.canvasWidgets[widget.parentId];
    const newParentWidget = this.context.canvasWidgets[relativeWidget.parentId];
    const parentLayout = newParentWidget?.layout ?? CanvasLayout.FIXED;

    if (!oldParentWidget || !newParentWidget || !newParentWidget.children) {
      return;
    }
    switch (position) {
      case "inside": {
        const oldParentId = oldParentWidget.widgetId;
        if (!widget || !relativeWidget || !oldParentId) {
          return;
        }
        const nearestCanvas = findClosestCanvas(
          relativeToWidgetId,
          this.context.canvasWidgets as CanvasWidgetsReduxState,
          {
            // in most slideouts and modals, the first section is the header, so we want to insert the new widget into the second section
            sectionInsertionPosition: 1,
          },
        );

        if (!nearestCanvas) {
          return;
        }
        const parentLayout = nearestCanvas.layout ?? CanvasLayout.FIXED;
        if (parentLayout === CanvasLayout.FIXED) {
          const newPosition = this.getGridPosition({
            widget,
            parentWidget: nearestCanvas,
            relativeWidget: nearestCanvas,
            oldParentWidget: oldParentWidget,
            position: "inside",
          });

          if (!newPosition) {
            return;
          }
          this.moveWidgetFn({
            widgetId,
            parentId: oldParentId,
            position: {
              left: newPosition.left,
              top: newPosition.top,
            },
            size: {
              height: newPosition.height ?? widget.height,
              width: newPosition.width ?? widget.width,
            },
            newParentId: nearestCanvas.widgetId,
          });
        } else {
          this.moveWidgetFn({
            widgetId,
            parentId: oldParentId,
            newParentId: nearestCanvas.widgetId,
            position: {
              top: widget.top ?? Dimension.gridUnit(0),
              left: widget.left ?? Dimension.gridUnit(0),
            },
            newChildIndex: nearestCanvas.children?.length ?? 0,
          });
        }
        break;
      }
      case "above":
      case "below":
      case "left":
      case "right":
        if (
          parentLayout === CanvasLayout.VSTACK ||
          parentLayout === CanvasLayout.HSTACK
        ) {
          const isBefore = position === "above" || position === "left";
          // figure out where the relative widget is in the parent
          const relativeWidgetIndex =
            newParentWidget.children?.indexOf(relativeToWidgetId);
          if (relativeWidgetIndex === -1 || relativeWidgetIndex == null) {
            return;
          }
          const newChildIndex = isBefore
            ? Math.max(0, relativeWidgetIndex - 1)
            : relativeWidgetIndex + 1;
          this.moveWidgetFn({
            widgetId,
            parentId: widget.parentId,
            position: {
              top: widget.top ?? Dimension.gridUnit(0),
              left: widget.left ?? Dimension.gridUnit(0),
            },
            newChildIndex,
            newParentId: newParentWidget.widgetId ?? "",
          });
        } else {
          const newPosition = this.getGridPosition({
            parentWidget: newParentWidget,
            oldParentWidget,
            relativeWidget,
            widget,
            position,
          });

          if (!newPosition) {
            return;
          }
          this.moveWidgetFn({
            widgetId,
            parentId: widget.parentId,
            position: {
              left: newPosition.left,
              top: newPosition.top,
            },
            size: {
              height: newPosition.height ?? widget.height,
              width: newPosition.width ?? widget.width,
            },
            newParentId: newParentWidget.widgetId ?? "",
          });
        }
        break;
    }
    this.widgetMovesById[widgetId] = {
      position,
      relativeTo: relativeWidget?.widgetName ?? "",
    };
  }
}
